// Chrome on Android plays a call through the "communication device" its microphone was opened
// on, and lists those devices as microphones with fixed English names (Chromium's
// CommunicationDeviceSelector). Some phones (seen on a Galaxy S22+) start the call on the
// earpiece, so the interviewer sounds like a phone call held away from the ear. Opening the mic
// on "Speakerphone" puts the interviewer on the loudspeaker; "Headset earpiece" moves it back.
// Other browsers don't list these, so everything here is a no-op there.
const SPEAKERPHONE = "Speakerphone";
const EARPIECE = "Headset earpiece";
const HEADSETS = new Set(["Wired headset", "Bluetooth headset", "USB audio"]);

export type PhoneAudio = {
  speakerId: string;
  earpieceId: string | null;
  // Headphones are plugged in or paired: leave the route to Chrome, which prefers them.
  headset: boolean;
};

/** Needs mic permission first: device names are blank until then. */
export const findPhoneAudio = async (): Promise<PhoneAudio | null> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const inputs = devices.filter((device) => device.kind === "audioinput");
    const speaker = inputs.find((device) => device.label === SPEAKERPHONE);
    if (!speaker?.deviceId) return null;
    return {
      speakerId: speaker.deviceId,
      earpieceId: inputs.find((device) => device.label === EARPIECE)?.deviceId ?? null,
      headset: inputs.some((device) => HEADSETS.has(device.label)),
    };
  } catch {
    return null;
  }
};

/** The mic to open for the call: the loudspeaker route on Android phones, else the default. */
export const callMicOptions = (micOn: boolean, phoneAudio: PhoneAudio | null) => {
  if (!micOn) return false;
  if (!phoneAudio || phoneAudio.headset) return true;
  // "ideal": if the route has gone, the default mic still opens.
  return { deviceId: { ideal: phoneAudio.speakerId } };
};
